import { CustomLayers } from "ags/names";
import AnimatedSprite from "slotworks/graphics/animation/animated-sprite";
import CanvasManager, { Orientation } from "slotworks/graphics/canvas/canvas-manager";
import Layers from "slotworks/graphics/layers/layers";
import soundManager from "slotworks/sound/sound-manager";
import Contract from "slotworks/utils/contracts/contract";
import { sequence } from "slotworks/utils/race-utils";

export interface FireworkSequenceConfig {
    portrait: {
        startBottomAfterXTopFrames: number,
        resolveAfterXBottomFrames: number
    };
    landscape: {
        resolveAfterXFrames: number
    };
}

class FireworksExplosionComponent {

    private portraitOne: AnimatedSprite;
    private portraitTwo: AnimatedSprite;
    private landscapeOne: AnimatedSprite;

    private layer: Layers;
    private config: FireworkSequenceConfig = {
        portrait: {
            startBottomAfterXTopFrames: 21,
            resolveAfterXBottomFrames: 13
        },
        landscape: {
            resolveAfterXFrames: 10
        }
    };

    private resolve: (value?: void) => void;

    public start(config?: FireworkSequenceConfig, out: boolean = false) {

        this.config = config || this.config;

        this.layer = Layers.Custom.get(CustomLayers.FIREWORK_EXPLOSION);
        Layers.BetBar.moveAbove(this.layer);
        Layers.SmartButton.moveAbove(Layers.BetBar);

        return sequence([
            () => this.layer.setScene("fireworks"),
            () => this.play(out)
        ]);
    }

    private setup() {
        CanvasManager.onOrientationChange.add(this.switchOrientation);
    }

    private play(out: boolean = false) {
        return new Contract<void>((resolve) => {

            const suffix = out ? "_out" : "";

            this.portraitOne = this.layer.getAnimatedSprite("fireworks_transition" + suffix);
            this.portraitTwo = this.layer.getAnimatedSprite("fireworks_transition_3" + suffix);
            this.landscapeOne = this.layer.getAnimatedSprite("fireworks_transition_short" + suffix);

            if (!this.portraitOne) {
                resolve();
                return;
            }

            this.setup();

            soundManager.customEvent("pick_transition_sfx");

            if (this.portraitTwo) {
                this.portraitTwo.stop();
            }
            this.landscapeOne.stop();
            this.portraitOne.stop();

            let played = false;

            this.resolve = resolve;

            this.portraitOne.onFrameChange.add((frame: number) => {

                const passedFirstAnimationThreshold = frame > this.config.portrait.startBottomAfterXTopFrames;

                if (passedFirstAnimationThreshold && !played) {
                    played = true;

                    if (this.portraitTwo) {
                        this.portraitTwo.onComplete.add(() => {
                            this.destroyAll();
                        });

                        this.portraitTwo.onFrameChange.add(() => {
                            this.endCheck();
                        });
                        this.portraitTwo.gotoAndPlay(0);
                    } else {
                        this.endCheck();
                    }
                }
            });
            this.portraitOne.gotoAndPlay(0);

            this.landscapeOne.onFrameChange.add(() => {
                this.endCheck();
            });

            this.landscapeOne.onComplete.add(() => {
                this.destroyAll();
            });
            this.landscapeOne.gotoAndPlay(0);

            this.switchOrientation(CanvasManager.orientation);
        });
    }

    private switchOrientation = (orientation: Orientation) => {
        switch (orientation) {
            case Orientation.LANDSCAPE:
                if (this.portraitOne) {
                    this.portraitOne.visible = false;

                    if (this.portraitTwo) {
                        this.portraitTwo.visible = false;
                    }
                    this.landscapeOne.visible = true;
                }
                break;
            case Orientation.PORTRAIT:
                if (this.portraitOne) {
                    this.portraitOne.visible = true;

                    if (this.portraitTwo) {
                        this.portraitTwo.visible = true;
                    }
                    this.landscapeOne.visible = false;
                }
                break;
        }
    }

    private endCheck() {
        if (this.resolve) {
            const isCurrentlyPortrait = CanvasManager.orientation === Orientation.PORTRAIT;
            const isCurrentlyLandscape = CanvasManager.orientation === Orientation.LANDSCAPE;

            const passedPortraitResolve = this.portraitOne ? this.portraitOne.currentFrame > this.config.portrait.resolveAfterXBottomFrames : true;
            const passedLandscapeResolve = this.landscapeOne.currentFrame > this.config.landscape.resolveAfterXFrames;

            if (isCurrentlyLandscape && passedLandscapeResolve || isCurrentlyPortrait && passedPortraitResolve) {
                this.resolve();
                this.resolve = null;
            }
        }
    }

    private destroyAll() {
        if (!this.landscapeOne || this.resolve) {
            return;
        }

        this.landscapeOne.destroy();
        this.portraitOne.destroy();
        if (this.portraitTwo) {
            this.portraitTwo.destroy();
        }

        this.landscapeOne = null;
        this.portraitTwo = null;
        this.portraitOne = null;

        CanvasManager.onOrientationChange.remove(this.switchOrientation);

        this.layer.defaultScene().execute();

        this.layer = null;
    }

}

export default new FireworksExplosionComponent();
